<div class="offer-rules-wrapper">
  <div class="header-row">
    <div class="header-copy">
      <h3>Offer Set Rules Configuration</h3>
      <p>Manage offer set rules and dollar discount rules for non-VIN offer sets. Changes remain local until rules are saved.</p>
    </div>
    <div class="header-actions">
      <p-button
        label="Save Rules"
        icon="pi pi-save"
        severity="success"
        [disabled]="getSaveRulesButtonDisabled()"
        [loading]="isSavingRules"
        (click)="requestSaveRules()">
      </p-button>
      <p-button
        label="Process Rules"
        severity="secondary"
        [disabled]="isProcessRulesButtonDisabled"
        [loading]="isProcessingRules"
        (click)="requestProcessRules()">
      </p-button>
      <p-button
        label="New Rule"
        icon="pi pi-plus"
        severity="success"
        [disabled]="getRuleMutationDisabled()"
        (click)="openCreateDialog($event)">
      </p-button>
    </div>
  </div>

  <div class="state-messages" aria-live="polite">
    @if (tableLoading) {
      <p-message
        severity="info"
        text="Loading offer rules.">
      </p-message>
    }

    @if (isOfferRegistryProcessingActive) {
      <p-message
        severity="info"
        text="Rule processing is currently running. Rule controls are disabled until processing completes.">
      </p-message>
    }

    @if (hasUnsavedRuleChanges) {
      <p-message
        severity="warn"
        text="You have unsaved rule changes.">
      </p-message>
    }

    @if (!changeSetValidation.isValid && changeSetValidation.operationCount > 0) {
      <p-message
        severity="error"
        [text]="changeSetValidation.errors.join(' ')">
      </p-message>
    }

    @if (hasRuleLoadFailed) {
      <p-message
        severity="error"
        text="Unable to load offer rules. Refresh the page to try again.">
      </p-message>
    }
  </div>

  <p-card class="table-card">
    <p-toolbar>
      <ng-template pTemplate="left">
        <span class="search-field">
          <p-iconField iconPosition="left">
            <p-inputIcon class="pi pi-search"></p-inputIcon>
            <input
              pInputText
              type="search"
              [(ngModel)]="searchValue"
              (input)="onSearch(searchValue)"
              placeholder="Search rules"
              aria-label="Search rules" />
          </p-iconField>
        </span>
      </ng-template>
      <ng-template pTemplate="right">
        <p-selectButton
          [options]="ruleTypeFilterOptions"
          [(ngModel)]="selectedRuleTypeFilter"
          (onChange)="onRuleTypeFilterChange($event.value)"
          optionLabel="label"
          optionValue="value"
          ariaLabelledBy="rule-type-filter-label">
        </p-selectButton>
        <span id="rule-type-filter-label" class="visually-hidden">Filter by rule type</span>
      </ng-template>
    </p-toolbar>

    <div class="table-section">
      <h4>Public Rules</h4>
      <p-table
        [value]="filteredPublicRules"
        [scrollable]="true"
        [loading]="tableLoading"
        dataKey="clientKey"
        class="custom-table"
        ariaLabel="Public offer rules">
        <ng-template pTemplate="header">
          <tr>
            <th>Rule</th>
            <th>Change</th>
            <th>Status</th>
            <th>Vehicle Criteria</th>
            <th>Max Price</th>
            <th>Last Update</th>
            <th>Actions</th>
          </tr>
        </ng-template>
        <ng-template pTemplate="body" let-row>
          <tr [class.pending-deletion-row]="row.changeState === ruleChangeStates.deleted">
            <td>
              <div class="rule-name">
                @if (row.rule.offerSetId) {
                  <a [href]="getOfferSetLink(row.rule)" target="_blank" rel="noopener noreferrer">
                    {{ getRuleName(row.rule) }}
                  </a>
                } @else {
                  <span class="discount-label">{{ getRuleName(row.rule) }}</span>
                }
              </div>
            </td>
            <td>
              @if (getChangeStateText(row.changeState); as changeText) {
                <span class="change-state" [ngClass]="row.changeState" [attr.aria-label]="'Unsaved state: ' + changeText">
                  {{ changeText }}
                </span>
              }
            </td>
            <td>
              <div class="status-indicator">
                <span class="status-dot" [ngClass]="getRuleStatusClass(row.rule)" aria-hidden="true"></span>
                <span class="status-label">{{ getRuleStatusLabel(row.rule) }}</span>
              </div>
            </td>
            <td>
              <div class="criteria-tags">
                @for (tag of row.vehicleCriteriaTags; track $index) {
                  <p-tag [value]="tag"></p-tag>
                }
              </div>
            </td>
            <td>
              @if (row.rule.maxSalePrice !== null && row.rule.maxSalePrice !== undefined) {
                <span>{{ row.rule.maxSalePrice | currency:'USD':'symbol':'1.0-0' }}</span>
              } @else {
                —
              }
            </td>
            <td><div class="timestamp">{{ getLatestTimestampDisplay(row.rule) }}</div></td>
            <td class="actions-cell">
              <div class="action-buttons">
                @if (row.changeState === ruleChangeStates.deleted) {
                  <p-button
                    label="Undo"
                    severity="secondary"
                    [disabled]="getRuleMutationDisabled()"
                    (click)="undoDelete(row)">
                  </p-button>
                } @else {
                  <p-button
                    icon="pi pi-pencil"
                    severity="secondary"
                    [ariaLabel]="'Edit ' + getRuleName(row.rule)"
                    [disabled]="getRuleMutationDisabled()"
                    (click)="openEditDialog(row, $event)">
                  </p-button>
                  <p-button
                    icon="pi pi-trash"
                    severity="danger"
                    [ariaLabel]="'Delete ' + getRuleName(row.rule)"
                    [disabled]="getRuleMutationDisabled()"
                    (click)="deleteRule(row)">
                  </p-button>
                  @if (row.changeState === ruleChangeStates.created || row.changeState === ruleChangeStates.updated) {
                    <p-button
                      label="Revert"
                      severity="secondary"
                      [disabled]="getRuleMutationDisabled()"
                      (click)="revertRule(row)">
                    </p-button>
                  }
                }
              </div>
            </td>
          </tr>
        </ng-template>
        <ng-template pTemplate="emptymessage">
          <tr><td colspan="7" class="empty">No public rules found.</td></tr>
        </ng-template>
      </p-table>
    </div>

    <div class="table-section">
      <h4>Private Rules</h4>
      <p-table
        [value]="filteredPrivateRules"
        [scrollable]="true"
        [loading]="tableLoading"
        dataKey="clientKey"
        class="custom-table"
        ariaLabel="Private offer rules">
        <ng-template pTemplate="header">
          <tr>
            <th>Rule</th>
            <th>Change</th>
            <th>Status</th>
            <th>Vehicle Criteria</th>
            <th>Max Price</th>
            <th>Last Update</th>
            <th>Actions</th>
          </tr>
        </ng-template>
        <ng-template pTemplate="body" let-row>
          <tr [class.pending-deletion-row]="row.changeState === ruleChangeStates.deleted">
            <td>
              <div class="rule-name">
                @if (row.rule.offerSetId) {
                  <a [href]="getOfferSetLink(row.rule)" target="_blank" rel="noopener noreferrer">
                    {{ getRuleName(row.rule) }}
                  </a>
                } @else {
                  <span class="discount-label">{{ getRuleName(row.rule) }}</span>
                }
              </div>
            </td>
            <td>
              @if (getChangeStateText(row.changeState); as changeText) {
                <span class="change-state" [ngClass]="row.changeState" [attr.aria-label]="'Unsaved state: ' + changeText">
                  {{ changeText }}
                </span>
              }
            </td>
            <td>
              <div class="status-indicator">
                <span class="status-dot" [ngClass]="getRuleStatusClass(row.rule)" aria-hidden="true"></span>
                <span class="status-label">{{ getRuleStatusLabel(row.rule) }}</span>
              </div>
            </td>
            <td>
              <div class="criteria-tags">
                @for (tag of row.vehicleCriteriaTags; track $index) {
                  <p-tag [value]="tag"></p-tag>
                }
              </div>
            </td>
            <td>
              @if (row.rule.maxSalePrice !== null && row.rule.maxSalePrice !== undefined) {
                <span>{{ row.rule.maxSalePrice | currency:'USD':'symbol':'1.0-0' }}</span>
              } @else {
                —
              }
            </td>
            <td><div class="timestamp">{{ getLatestTimestampDisplay(row.rule) }}</div></td>
            <td class="actions-cell">
              <div class="action-buttons">
                @if (row.changeState === ruleChangeStates.deleted) {
                  <p-button
                    label="Undo"
                    severity="secondary"
                    [disabled]="getRuleMutationDisabled()"
                    (click)="undoDelete(row)">
                  </p-button>
                } @else {
                  <p-button
                    icon="pi pi-pencil"
                    severity="secondary"
                    [ariaLabel]="'Edit ' + getRuleName(row.rule)"
                    [disabled]="getRuleMutationDisabled()"
                    (click)="openEditDialog(row, $event)">
                  </p-button>
                  <p-button
                    icon="pi pi-trash"
                    severity="danger"
                    [ariaLabel]="'Delete ' + getRuleName(row.rule)"
                    [disabled]="getRuleMutationDisabled()"
                    (click)="deleteRule(row)">
                  </p-button>
                  @if (row.changeState === ruleChangeStates.created || row.changeState === ruleChangeStates.updated) {
                    <p-button
                      label="Revert"
                      severity="secondary"
                      [disabled]="getRuleMutationDisabled()"
                      (click)="revertRule(row)">
                    </p-button>
                  }
                }
              </div>
            </td>
          </tr>
        </ng-template>
        <ng-template pTemplate="emptymessage">
          <tr><td colspan="7" class="empty">No private rules found.</td></tr>
        </ng-template>
      </p-table>
    </div>
  </p-card>
</div>

<auto-ipop-rule-workflow-rule-modal
  [visible]="dialogVisible"
  [dialogTitle]="dialogTitle"
  [mode]="dialogMode"
  [accountId]="accountId"
  [rule]="selectedRule"
  [existingDraft]="selectedDraft"
  (closed)="closeDialog()"
  (staged)="handleDialogStaged($event)">
</auto-ipop-rule-workflow-rule-modal>

<p-confirmDialog></p-confirmDialog>
